import { m } from '@hub/messages';
import { defineLayoutRoute, definePageRoute, defineRedirectRoute } from '@hub/router/definePageRoute';
import { createRoute, type DomainParents, defineDomainRoutes, redirect } from '@hub/router/domain-routes';
import { getRecentlyWatchedOptions, getUserOptions } from '@platform/api';
import { HomePaths } from '../home/route.paths';
import { createNotificationsOverviewRoute } from '../notifications';
import { NotificationPaths } from '../notifications/route.paths';
import { AccountPaths } from './route.paths';

export default function createAccountRoutes(parents: DomainParents) {
  const accountLayoutRoute = defineLayoutRoute(parents, {
    path: AccountPaths.account,
    breadcrumbLabel: m.account_breadcrumb_label,
  });

  // Without an index child, direct visits to /account would render the layout's
  // empty Outlet — there's no account dashboard today.
  const accountIndexRoute = createRoute({
    getParentRoute: () => accountLayoutRoute,
    path: AccountPaths.accountIndex.path,
    beforeLoad: () => {
      throw redirect({ to: HomePaths.home.path });
    },
  });

  const privacyOverviewRoute = definePageRoute(parents, {
    path: AccountPaths.privacyOverview,
    title: m.account_privacy_title,
    description: m.account_privacy_description,
    page: () => import('./PrivacyOverview.page'),
    flag: 'page-privacy',
    pendingComponent: () => import('./components/PrivacyOverviewSkeleton'),
    query: getRecentlyWatchedOptions,
    quickAction: {
      to: AccountPaths.privacyOverview.path,
      icon: 'privacy',
      title: m.account_privacy_quick_action_title,
      subline: m.account_privacy_quick_action_subline,
      surfaces: ['home-privacy'],
      order: 25,
    },
  });

  // Layout-only parent for the credentials subtree. Gives URL-nested routes
  // (`/account/credentials/update-{email,password}`) a true tree-level parent
  // so breadcrumbs fall out of the match chain. No flag — the read view is
  // gated by the index child, the edit pages by their own flag.
  const credentialsLayoutRoute = defineLayoutRoute(parents, {
    path: AccountPaths.credentials,
    breadcrumbLabel: m.account_credentials_title,
  });

  const credentialsIndexRoute = definePageRoute(parents, {
    parent: credentialsLayoutRoute,
    path: AccountPaths.credentialsIndex,
    title: m.account_credentials_title,
    description: m.account_credentials_description,
    page: () => import('./Credentials.page'),
    flag: 'page-account-credentials',
    query: getUserOptions,
    quickAction: {
      to: AccountPaths.credentials.path,
      icon: 'credentials',
      title: m.account_credentials_quick_action_title,
      subline: m.account_credentials_quick_action_subline,
      surfaces: ['home-account', 'invoices-actions', 'payment-change-actions', 'notifications-actions'],
      order: 20,
    },
  });

  const updateEmailRoute = definePageRoute(parents, {
    parent: credentialsLayoutRoute,
    path: AccountPaths.updateEmail,
    title: m.account_credentials_update_email_title,
    description: m.account_credentials_update_email_description,
    page: () => import('./EditEmail.page'),
    flag: 'account-credentials-change-enabled',
    backFallback: AccountPaths.credentials.path,
  });

  const updatePasswordRoute = definePageRoute(parents, {
    parent: credentialsLayoutRoute,
    path: AccountPaths.updatePassword,
    title: m.account_credentials_update_password_title,
    // No description on the route — EditPassword.page renders the rich-text version itself
    // because it embeds an <a> link, which the static `() => string` description slot can't carry.
    page: () => import('./EditPassword.page'),
    flag: 'account-credentials-change-enabled',
    backFallback: AccountPaths.credentials.path,
  });

  credentialsLayoutRoute.addChildren([credentialsIndexRoute, updateEmailRoute, updatePasswordRoute]);

  const activationRoute = definePageRoute(parents, {
    path: AccountPaths.activation,
    title: m.account_activation_page_title,
    page: () => import('./Activation.page'),
    flag: 'page-account-activation',
    // The verify step is an explicit user action, so there's no token-resolution query on mount;
    // preload-on-hover is still off to keep the public page out of authenticated prefetching.
    preload: false,
  });

  const personalDataRoute = definePageRoute(parents, {
    path: AccountPaths.personalData,
    title: m.account_personal_data_title,
    description: m.account_personal_data_description,
    page: () => import('./PersonalData.page'),
    flag: 'page-personal-data',
    pendingComponent: () => import('./components/PersonalDataSkeleton'),
    query: getUserOptions,
    quickAction: {
      to: AccountPaths.personalData.path,
      icon: 'personal-data',
      title: m.account_personal_data_quick_action_title,
      subline: m.account_personal_data_quick_action_subline,
      surfaces: ['home-account', 'device-actions', 'notifications-actions'],
      order: 25,
    },
  });

  const notificationsRoute = createNotificationsOverviewRoute(parents, accountLayoutRoute);

  accountLayoutRoute.addChildren([accountIndexRoute, notificationsRoute]);

  const legacyNotificationsOverviewRedirect = defineRedirectRoute(parents, {
    from: NotificationPaths.legacyOverview,
    to: NotificationPaths.overview,
  });

  return defineDomainRoutes({
    authenticated: [
      accountLayoutRoute,
      privacyOverviewRoute,
      credentialsLayoutRoute,
      personalDataRoute,
      legacyNotificationsOverviewRedirect,
    ] as const,
    public: [activationRoute] as const,
  });
}
